import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode, ResolvedPos } from "@tiptap/pm/model";
import { Selection, TextSelection, type Transaction } from "@tiptap/pm/state";
import { canJoin } from "@tiptap/pm/transform";
import { storedAsOneList } from "@/lib/memoDocFidelity";

const LIST_TYPES = new Set(["bulletList", "orderedList"]);

const isList = (node: ProseMirrorNode | null | undefined): boolean =>
  !!node && LIST_TYPES.has(node.type.name);

/** Whether a node of this type encloses the position. */
function inside($pos: ResolvedPos, typeName: string): boolean {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === typeName) return true;
  }
  return false;
}

/**
 * Where text joined onto the end of a list or list item from below goes: the last
 * visible line of the list's deepest last item. Walks down the last item of every
 * nested list. `lineEnd` is the end of that item's last line when its last block is a
 * line of text; null when it is a screenshot, a table, a quote or code, where text does
 * not belong: then the joined text becomes a new paragraph below it, at `itemEnd` (the
 * end of that item's content), so it never jumps above the screenshot.
 */
function lastLineOf(
  node: ProseMirrorNode,
  pos: number,
): { lineEnd: number | null; itemEnd: number } {
  let current = node;
  let start = pos;
  for (;;) {
    const end = start + current.nodeSize - 1;
    const last = current.lastChild;
    if (!last) return { lineEnd: null, itemEnd: end };
    const lastStart = end - last.nodeSize;
    if (isList(current) || isList(last)) {
      // A list: its last item. An item that ends in a list: that list.
      current = last;
      start = lastStart;
      continue;
    }
    const isLine = last.isTextblock && !last.type.spec.code;
    return { lineEnd: isLine ? lastStart + 1 + last.content.size : null, itemEnd: end };
  }
}

/** Joins the two lists at `pos` into one when they are of one kind (bullet or numbered). */
function joinListsAt(tr: Transaction, pos: number): void {
  const $pos = tr.doc.resolve(pos);
  const { nodeBefore, nodeAfter } = $pos;
  // A bullet list and a numbered list stay two lists (canJoin alone would merge the
  // items of one into the other).
  if (isList(nodeBefore) && nodeAfter?.type === nodeBefore?.type && canJoin(tr.doc, pos)) {
    tr.join(pos);
  }
}

/**
 * Joins the two lists at `pos` into one only when that changes nothing the writer sees
 * (`storedAsOneList`): two bullet lists, which the save merges anyway (stored apart they
 * reload as one loose list), or two numbered lists whose numbering continues (one list,
 * the same numbers). Two numbered lists left apart are saved apart and reload as two, so
 * either way the editor and the saved point agree.
 */
function joinListsStoredAsOne(tr: Transaction, pos: number): void {
  const { nodeBefore, nodeAfter } = tr.doc.resolve(pos);
  if (nodeBefore && nodeAfter && storedAsOneList(nodeBefore, nodeAfter) && canJoin(tr.doc, pos)) {
    tr.join(pos);
  }
}

/**
 * Protokoll entries only (RichTextEditor `variant="memo"`): lists behave like
 * Word, and Tab never moves focus out of the editor.
 *
 * - Backspace right after an input rule (typing "- " or "1. " opened a list) undoes
 *   it and brings the typed characters back, TipTap's standard; the core keymap
 *   would do that first, but these shortcuts run before it.
 * - Backspace at the very start of a top-level list item that has sub-items and an
 *   item above joins it onto that item: its text continues the last line above, its
 *   sub-items stay at their level (they join the sub-list above when there is one).
 *   Lifting it instead promoted the sub-items and split the list in two (and two bullet
 *   lists side by side are stored apart and reload as ONE loose list).
 * - Backspace at the very start of any other list item outdents it (nested item) or
 *   turns it into a paragraph (top-level item). Without this, Backspace there
 *   runs join-backward and the item becomes a second paragraph of the item
 *   above: a sub-bullet is merged away instead of moved one level up. When a first
 *   top-level item with sub-items becomes a paragraph, its sub-items and the rest of its
 *   list become one list when they are of one kind (numbered too: "1. c, 2. e"); a
 *   different list below a last item joins them only when that changes nothing the
 *   writer sees (bullet lists; numbered lists only when the numbering continues), so it
 *   keeps its own numbering. Only the lists at the lifted item's end are joined, never
 *   lists elsewhere.
 *   Shift+Backspace and Mod+Backspace (Ctrl, Cmd on a Mac) do the same there: the
 *   core keymap binds both to its Backspace handler, and writers often still hold
 *   Shift after a capital letter.
 * - Backspace at the start of a paragraph that follows a list joins it onto the end
 *   of the list's last visible line; a blank line there is removed and the caret goes
 *   to the end of that line (Word: the bullet goes first, then the line merges up).
 *   When the list's last item ends in a screenshot (a table, a quote, code), the text
 *   becomes a new line below it in that item. A list right below the removed paragraph
 *   joins the list above when they are of one kind after a blank line, but after a line
 *   of text only when that changes nothing the writer sees (a numbered list restarting
 *   below the text keeps its numbers). Without this, join-backward turns the
 *   paragraph back into an item of the list, whose next Backspace lifts it out again:
 *   the two rules undo each other and Backspace toggles forever. Everywhere else
 *   Backspace falls through.
 * - Tab / Shift+Tab indent and outdent inside a list and are swallowed
 *   everywhere else, also when the indent or outdent is impossible (the first
 *   item cannot be indented). Unhandled, the browser moves focus out of the
 *   editor, and a memo entry commits and closes on blur.
 * - Inside a table, Tab and Shift+Tab are left to the table keymap, which moves
 *   between cells.
 *
 * Load-bearing: priority 1000 puts these shortcuts ahead of every keymap at the
 * default priority 100 (the core Backspace with its join-backward, the list
 * item's and the table's Tab), wherever the extensions are registered. On a
 * priority tie TipTap runs the later-registered keymap first, so a list keymap
 * added later at the default priority would otherwise claim Backspace before us.
 * They do not run before RichTextEditor's own `editorProps.handleKeyDown`:
 * ProseMirror asks the view's props before any plugin, so its empty-document
 * Backspace (`onEmptyBackspace`, delete the entry) still wins, except right after
 * an input rule. The mention suggestion popup (priority 101) handles Enter, Escape
 * and the arrow keys, none of which are bound here.
 */
export const MemoListKeys = Extension.create({
  name: "memoListKeys",

  priority: 1000,

  addKeyboardShortcuts() {
    // The caret at offset 0 of a list item's FIRST paragraph. A later paragraph of the
    // same item keeps join-backward (it joins the paragraph above, inside the item).
    // The paragraph check also keeps a gap cursor between top-level blocks (depth 0,
    // no node above it) out of `node(-1)`.
    const atItemStart = (selection: Selection): boolean => {
      const { $from } = selection;
      return (
        selection.empty &&
        $from.parentOffset === 0 &&
        $from.parent.type.name === "paragraph" &&
        $from.node(-1).type.name === "listItem" &&
        $from.index(-1) === 0
      );
    };
    // Not nested in another list (in the document or a quote).
    const topLevel = ($from: ResolvedPos): boolean => $from.node(-3).type.name !== "listItem";

    const joinItemOntoItemAbove = () => {
      const { state, view } = this.editor;
      const { selection } = state;
      const { $from } = selection;
      if (!atItemStart(selection) || !topLevel($from) || $from.index(-2) === 0) return false;
      const item = $from.node(-1);
      let hasSubList = false;
      item.forEach((child) => {
        if (isList(child)) hasSubList = true;
      });
      if (!hasSubList) return false;

      const itemStart = $from.before(-1);
      const above = $from.node(-2).child($from.index(-2) - 1);
      const aboveStart = itemStart - above.nodeSize;
      const aboveEnd = itemStart - 1;
      const { lineEnd, itemEnd } = lastLineOf(above, aboveStart);
      const text = $from.parent.content;
      // Everything after the item's first line: its sub-lists (and whatever else it holds).
      const rest = item.content.cut($from.parent.nodeSize);

      const tr = state.tr.delete(itemStart, itemStart + item.nodeSize);
      // The sub-items keep their level: they go to the end of the item above, into its
      // own sub-list when that ends it and is of the same kind.
      tr.insert(aboveEnd, rest);
      joinListsAt(tr, aboveEnd);
      // The text continues the line above. Everything changed so far lies after it.
      if (lineEnd !== null) {
        tr.insert(lineEnd, text);
        tr.setSelection(TextSelection.create(tr.doc, lineEnd));
      } else if (text.size > 0) {
        tr.insert(itemEnd, $from.parent.copy(text));
        tr.setSelection(TextSelection.create(tr.doc, itemEnd + 1));
      } else {
        const $end = tr.doc.resolve(itemEnd);
        tr.setSelection(Selection.findFrom($end, -1, true) ?? Selection.near($end, -1));
      }
      view.dispatch(tr.scrollIntoView());
      return true;
    };
    const liftAtItemStart = () => {
      const { selection } = this.editor.state;
      if (!atItemStart(selection)) return false;
      const { $from } = selection;
      // Claimed even if the lift could not run: falling through would let
      // join-backward merge the item into the one above.
      if (!topLevel($from)) {
        this.editor.commands.liftListItem("listItem");
        return true;
      }
      const item = $from.node(-1);
      const caret = $from.pos;
      // Whether more items of the item's own list follow it (read before the lift).
      const sameListBelow = $from.index(-2) < $from.node(-2).childCount - 1;
      this.editor
        .chain()
        .liftListItem("listItem")
        .command(({ tr }) => {
          if (!tr.docChanged) return true;
          // The item's blocks now sit where the item was, its first line first, so only
          // their end can meet a list: the item's own sub-list (a first item with
          // sub-items) and the rest of the list below it, or a list right below a last
          // item. Lists anywhere else in the point are left as they are.
          // - The rest of its own list: one list with the sub-list, of the same kind
          //   (Word: "1. c, 2. e"). The lift restarts that rest at the list's own start, so
          //   a numbering check would keep "1. c" and "1. e" apart.
          // - A different list below a last item: one list only when that changes nothing
          //   the writer sees (storedAsOneList), so it keeps its own numbering.
          const lifted = tr.doc.resolve(tr.mapping.map(caret)).before();
          const end = lifted + item.content.size;
          if (sameListBelow) joinListsAt(tr, end);
          else joinListsStoredAsOne(tr, end);
          return true;
        })
        .run();
      return true;
    };
    const joinOntoListAbove = () => {
      const { state, view } = this.editor;
      const { selection } = state;
      const { $from } = selection;
      // The caret at offset 0 of a paragraph whose previous sibling is a list, in
      // any container (the document, a quote, a list item).
      if (!selection.empty || $from.parentOffset !== 0) return false;
      if ($from.parent.type.name !== "paragraph" || $from.index(-1) === 0) return false;
      const list = $from.node(-1).child($from.index(-1) - 1);
      if (!isList(list)) return false;

      const paragraphStart = $from.before();
      const { lineEnd, itemEnd } = lastLineOf(list, paragraphStart - list.nodeSize);
      const { content } = $from.parent;
      // Everything inserted below lies before the deleted paragraph.
      const tr = state.tr.delete(paragraphStart, $from.after());
      if (lineEnd !== null) {
        tr.insert(lineEnd, content);
        tr.setSelection(TextSelection.create(tr.doc, lineEnd));
      } else if (content.size > 0) {
        // The last item ends in a screenshot: the text goes below it, as its own line.
        tr.insert(itemEnd, $from.parent.copy(content));
        tr.setSelection(TextSelection.create(tr.doc, itemEnd + 1));
      } else {
        // A blank line: removed, the caret goes to the end of the last line of text.
        const $end = tr.doc.resolve(itemEnd);
        tr.setSelection(Selection.findFrom($end, -1, true) ?? Selection.near($end, -1));
      }
      // The paragraph is gone, so the two lists it separated may now touch.
      // - It was a blank line: they become one list whenever they are of one kind (a list
      //   split by an accidental Enter is whole again, its numbers whatever they became).
      // - It was a line of text: one list only when that changes nothing the writer sees
      //   (storedAsOneList: two bullet lists, which the save merges anyway; two numbered
      //   lists only when the second continues the first). A numbered list that restarts
      //   below the text keeps its own numbering.
      if (content.size === 0) joinListsAt(tr, tr.mapping.map(paragraphStart));
      else joinListsStoredAsOne(tr, tr.mapping.map(paragraphStart));
      view.dispatch(tr.scrollIntoView());
      return true;
    };
    const backspace = () =>
      this.editor.commands.undoInputRule() ||
      joinItemOntoItemAbove() ||
      liftAtItemStart() ||
      joinOntoListAbove();
    return {
      Backspace: backspace,
      "Shift-Backspace": backspace,
      "Mod-Backspace": backspace,
      Tab: () => {
        const { $from } = this.editor.state.selection;
        if (inside($from, "table")) return false;
        if (inside($from, "listItem")) {
          return this.editor.commands.sinkListItem("listItem") || true;
        }
        return true;
      },
      "Shift-Tab": () => {
        const { $from } = this.editor.state.selection;
        if (inside($from, "table")) return false;
        if (inside($from, "listItem")) {
          return this.editor.commands.liftListItem("listItem") || true;
        }
        return true;
      },
    };
  },
});
